/**
 * Payroll (Fan, 2026-10-27): its own page. Payroll is run by the accountants;
 * this page reads their monthly emails (payslips, P32, what is owed to HMRC
 * and the pension). Each month's HMRC amount is also a tax debt on the Tax
 * Reserves pages, so both pages show the same figure.
 *
 * Stat cards (Fan, 2026-10-27): figures come from GET /payroll/overview
 * `stats` (computed in Decimal on the backend), never summed here.
 */
import { useEffect, useState } from 'react';
import { Users, Wallet, Landmark, CalendarClock } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { PayrollTaxDebtsPanel } from '../components/PayrollTaxDebtsPanel';
import { Card, CardContent } from '../components/ui/card';
import { payrollAPI } from '../lib/api';
import { useEntityStore } from '../store';

function gbp(v) {
  if (v === null || v === undefined || v === '') return '-';
  return `\u00A3${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '-';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Stat({ icon: Icon, label, value, sub, tone, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${tone || 'bg-primary/10'}`}>
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold font-mono tracking-tight">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PayrollStatCards({ entityId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let live = true;
    payrollAPI.getOverview(entityId || undefined)
      .then((res) => { if (live) setStats(res.data?.stats || null); })
      .catch(() => { if (live) setStats(null); });
    return () => { live = false; };
  }, [entityId]);
  if (!stats) return null;
  const overdue = Number(stats.hmrc_overdue || 0) > 0;
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="payroll-stat-cards">
      <Stat
        icon={Wallet}
        label={`Payroll cost (${stats.year})`}
        value={gbp(stats.cost_ytd)}
        sub={`${stats.months} months: gross ${gbp(stats.gross_ytd)} + employer NI and pension ${gbp(stats.employer_costs_ytd)}`}
        testId="payroll-stat-cost"
      />
      <Stat
        icon={Users}
        label="People on payroll"
        value={String(stats.headcount)}
        sub={stats.latest_label || ''}
        testId="payroll-stat-headcount"
      />
      <Stat
        icon={CalendarClock}
        label="Net pay, latest month"
        value={gbp(stats.latest_net)}
        sub={stats.latest_pay_date ? `Pay date ${fmtDate(stats.latest_pay_date)}` : ''}
        testId="payroll-stat-net"
      />
      <Stat
        icon={Landmark}
        label="Owed to HMRC (PAYE and NI)"
        value={gbp(stats.hmrc_open)}
        tone={overdue ? 'bg-red-100' : undefined}
        sub={overdue
          ? `${gbp(stats.hmrc_overdue)} overdue`
          : (stats.next_hmrc_due ? `Next ${gbp(stats.next_hmrc_amount)} by ${fmtDate(stats.next_hmrc_due)}` : 'Nothing due')}
        testId="payroll-stat-hmrc"
      />
    </div>
  );
}

export function PayrollPage() {
  const { selectedEntity } = useEntityStore();
  const entityId = selectedEntity && selectedEntity !== 'all' ? selectedEntity : null;
  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="payroll-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payroll</h1>
          <p className="text-muted-foreground mt-1">
            Monthly payroll from the accountants. What is owed to HMRC also shows under Tax debts on Tax Reserves.
          </p>
        </div>
        <PayrollStatCards entityId={entityId} />
        <PayrollTaxDebtsPanel entityId={entityId} show="payroll" />
      </div>
    </DashboardLayout>
  );
}

export default PayrollPage;
